import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ReactLenis, useLenis } from 'lenis/react';
import { useLocation } from 'react-router-dom';
import { Motif } from './Ornaments';

const EDITORIAL_EASE = [0.22, 1, 0.36, 1];
const SCROLL_DELAYS_MS = 70;
const HASH_SCROLL_RETRIES_MS = [80, 320, 700];

const REVEAL_HIDDEN = { opacity: 0, y: 18 };
const REVEAL_SHOWN = { opacity: 1, y: 0 };
const REVEAL_VIEWPORT = { once: true, amount: 0.08 };

const PAGE_HIDDEN = { opacity: 0, y: 12 };
const PAGE_SHOWN = { opacity: 1, y: 0 };
const PAGE_TRANSITION = { duration: 0.4, ease: EDITORIAL_EASE };
const PAGE_TRANSITION_REDUCED = { duration: 0, ease: EDITORIAL_EASE };

const lenisOptions = (reduced) => ({
  lerp: 0.085,
  smoothWheel: !reduced,
  syncTouch: false,
  anchors: reduced ? { immediate: true } : true,
});

const cursorStateFor = (target) => {
  if (!(target instanceof Element)) return 'normal';
  if (target.closest('[data-project]')) return 'project';
  if (target.closest('a,button,[role="button"]')) return 'link';
  return 'normal';
};

export const Reveal = ({ children, className = '', delay = 0, ...props }) => {
  const reduced = useReducedMotion();
  return <motion.div className={className} initial={reduced ? false : REVEAL_HIDDEN}
    whileInView={REVEAL_SHOWN} viewport={REVEAL_VIEWPORT}
    transition={{ duration: 0.65, delay, ease: EDITORIAL_EASE }} {...props}>{children}</motion.div>;
};

export const PageEntrance = ({ children }) => {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  return <motion.div key={pathname} data-testid="page-transition"
    initial={reduced ? false : PAGE_HIDDEN}
    animate={PAGE_SHOWN}
    transition={reduced ? PAGE_TRANSITION_REDUCED : PAGE_TRANSITION}>{children}</motion.div>;
};

export const SmoothScroll = ({ children }) => {
  const reduced = useReducedMotion();
  return <ReactLenis root options={lenisOptions(reduced)}>
    {children}
  </ReactLenis>;
};

export const RouteScroll = () => {
  const { pathname, hash } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    const go = () => {
      const target = hash ? document.getElementById(hash.slice(1)) : 0;
      if (target === null) return;
      if (lenis) lenis.scrollTo(target, { immediate: true });
      else if (target === 0) window.scrollTo(0, 0);
      else target.scrollIntoView();
    };
    const delays = hash ? HASH_SCROLL_RETRIES_MS : [SCROLL_DELAYS_MS];
    const timers = delays.map(delay => setTimeout(go, delay));
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash, lenis]);
  return null;
};

export const CustomCursor = () => {
  const ref = useRef(null);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');
    if (!query.matches) return;
    const cursor = ref.current;
    const move = e => {
      if (e.pointerType === 'touch') return;
      cursor.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      cursor.dataset.visible = 'true';
      cursor.dataset.state = cursorStateFor(e.target);
    };
    const hide = () => { cursor.dataset.visible = 'false'; };
    document.documentElement.classList.add('custom-cursor-on');
    window.addEventListener('pointermove', move, { passive: true });
    document.addEventListener('pointerleave', hide);
    window.addEventListener('blur', hide);
    return () => {
      document.documentElement.classList.remove('custom-cursor-on');
      window.removeEventListener('pointermove', move);
      document.removeEventListener('pointerleave', hide);
      window.removeEventListener('blur', hide);
    };
  }, []);
  return <div ref={ref} className="custom-cursor" data-visible="false" aria-hidden="true">
    <div className="cursor-inner"><Motif /><span>VIEW ↗</span></div>
  </div>;
};
